/**
 * SEO catalogue for the public surface. Imported by React (`useSeo`) *and* by
 * `vite.config.ts`, which emits `sitemap.xml`, `robots.txt` and `llms.txt`
 * from it at build time — keep this file free of browser-only APIs and of
 * `import.meta.env`.
 */

/** Public origin the portal is served from (no trailing slash). */
export const SITE_URL = 'https://app.cmsh.io';
export const SITE_NAME = 'CMSH.io';
export const SHORT_LINK_HOST = 'https://cmsh.io';
export const API_URL = 'https://api.cmsh.io';

export const DEFAULT_TITLE =
  'CMSH.io — URL shortener with realtime analytics, QR codes & API';
export const DEFAULT_DESCRIPTION =
  'CMSH.io is a URL shortener that measures everything: shorten, brand and track every link with realtime click analytics, QR codes, custom domains and a clean REST API. Free plan, no credit card.';

/** 1200×630 social card generated by `npm run brand-assets`. */
export const OG_IMAGE_PATH = '/og-image.png';
export const OG_IMAGE_ALT = 'CMSH.io — Short links that measure everything';

export const ORGANIZATION = {
  name: 'Codemina Ltd',
  url: 'https://www.codemina.com',
  email: 'info@codemina.co.uk',
} as const;

export type ChangeFreq =
  | 'always'
  | 'hourly'
  | 'daily'
  | 'weekly'
  | 'monthly'
  | 'yearly'
  | 'never';

export interface PublicPage {
  /** Route path, always starting with `/`. */
  path: string;
  title: string;
  description: string;
  /** ISO date (YYYY-MM-DD) of the last content change; omitted when unknown. */
  lastmod?: string;
  changefreq: ChangeFreq;
  priority: number;
}

/**
 * Every indexable public route. This list *is* the sitemap and the set of
 * pages pre-rendered to static HTML after `vite build` (see scripts/prerender.mjs).
 * Auth flows, the signed-in app and legacy redirects are deliberately absent.
 */
export const PUBLIC_PAGES: PublicPage[] = [
  {
    path: '/',
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    changefreq: 'weekly',
    priority: 1,
  },
  {
    path: '/privacy-policy',
    title: 'Privacy Policy — CMSH.io',
    description:
      'How Codemina Ltd collects, uses and protects your data when you use the CMSH.io URL shortener, dashboard, API and short links.',
    lastmod: '2026-02-21',
    changefreq: 'yearly',
    priority: 0.3,
  },
  {
    path: '/terms-of-use',
    title: 'Terms of Use — CMSH.io',
    description:
      'The terms that govern your use of the CMSH.io URL shortening service, dashboard, API, subscription plans and short links.',
    lastmod: '2026-02-19',
    changefreq: 'yearly',
    priority: 0.3,
  },
];

/** Paths crawlers should stay out of: the signed-in app, auth flows and legacy client-side redirects. */
export const DISALLOWED_PATHS = [
  '/app',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/error',
  '/dev/',
  '/dashboard',
  '/links',
  '/analytics',
  '/api-keys',
  '/profile',
  '/subscription',
];

/**
 * AI/answer-engine crawlers we explicitly welcome (GEO). `User-agent: *` already
 * allows them; naming them makes the intent unambiguous for operators that
 * only honour a group addressed to them.
 */
export const AI_CRAWLERS = [
  'GPTBot',
  'OAI-SearchBot',
  'ChatGPT-User',
  'ClaudeBot',
  'Claude-SearchBot',
  'Claude-User',
  'anthropic-ai',
  'PerplexityBot',
  'Perplexity-User',
  'Google-Extended',
  'Applebot-Extended',
  'Amazonbot',
  'DuckAssistBot',
  'meta-externalagent',
  'CCBot',
];

/** `https://app.cmsh.io` + path (`/` → `https://app.cmsh.io/`). */
export const absoluteUrl = (path: string): string =>
  `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;

/** `pageTitle('Sign in')` → `Sign in — CMSH.io`. */
export const pageTitle = (title: string): string => `${title} — ${SITE_NAME}`;

export const publicPage = (path: string): PublicPage | undefined =>
  PUBLIC_PAGES.find((p) => p.path === path);
